import {
  normalizeCreatePartsOrderInput,
  normalizePartsOrderSearchInput,
  normalizeReceivePartsInput,
  normalizeSavePartsOrderInput,
  partsServiceMissingMessage,
  type CompletePunchoutInput,
  type CreatePartsOrderInput,
  type PartsGateway,
  type PartsOrderActionInput,
  type PartsOrderPage,
  type PartsOrderSearchInput,
  type PartsOrderValidation,
  type PartsOrderingService,
  type PartsOverview,
  type PartsStockShortfall,
  type PunchoutSessionHandle,
  type ReceivePartsInput,
  type ResolveReviewInput,
  type SavePartsOrderInput,
  type SaveSupplierConnectionInput,
  type StartPunchoutInput,
  type StockWorkOrderHandle,
  type SubmitPartsOrderInput,
  type SupplierConnectionRecord,
  type SupplierIntegrationEventRecord,
  type SupplierPurchaseOrderRecord,
} from '@forq/parts-data';
import type { ForqSupabaseClient } from './client';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function arrayOf<T>(value: unknown, operation: string): readonly T[] {
  if (!Array.isArray(value)) throw new SupabaseOperationError(operation, 'The response is not an array.');
  return value as readonly T[];
}

function objectOf<T>(value: unknown, operation: string): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'The response is not an object.');
  }
  return value as T;
}

/**
 * Ersatzteilbeschaffung gegen die echte Datenbank.
 *
 * Zweigeteilt, und das ist der Kern der ganzen Architektur:
 *
 *   Alles, was OHNE den Lieferanten geht -- lesen, Entwuerfe, stornieren, klaeren,
 *   Wareneingang --, laeuft als RPC direkt gegen Postgres. Die Rechte- und
 *   Mandantenpruefung steckt dort, nicht hier.
 *
 *   Alles, was den Lieferanten BERUEHRT -- Sitzung oeffnen, Warenkorb annehmen,
 *   Preise pruefen, absenden, Status abfragen --, geht ueber den FORQ-Server. Der
 *   Browser hat die Zugangsdaten nicht und die passenden Datenbankfunktionen sind
 *   ihm entzogen; ohne Server ist dieser Teil schlicht nicht verfuegbar.
 */
export class SupabasePartsGateway implements PartsGateway {
  constructor(
    private readonly client: ForqSupabaseClient,
    private readonly ordering: PartsOrderingService | null = null,
  ) {}

  private service(): PartsOrderingService {
    if (!this.ordering?.available) {
      throw new Error(partsServiceMissingMessage);
    }
    return this.ordering;
  }

  // --- Übersicht und Konfiguration ----------------------------------------

  async getOverview(tenantId: string): Promise<PartsOverview> {
    const { data, error } = await this.client.rpc('parts_overview', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('parts.overview', messageOf(error));
    return objectOf<PartsOverview>(data, 'parts.overview');
  }

  async listConnections(tenantId: string): Promise<readonly SupplierConnectionRecord[]> {
    const { data, error } = await this.client.rpc('parts_connection_list', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('parts.connections', messageOf(error));
    return arrayOf<SupplierConnectionRecord>(data ?? [], 'parts.connections');
  }

  async saveConnection(input: SaveSupplierConnectionInput): Promise<SupplierConnectionRecord> {
    const { data, error } = await this.client.rpc('parts_connection_save', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('parts.connection.save', messageOf(error));
    return objectOf<SupplierConnectionRecord>(data, 'parts.connection.save');
  }

  // --- Bestandsabgleich ----------------------------------------------------

  async checkWorkOrderStock(
    tenantId: string,
    workOrderId: string,
  ): Promise<readonly PartsStockShortfall[]> {
    const { data, error } = await this.client.rpc('parts_stock_check', {
      p_tenant_id: tenantId,
      p_work_order_id: workOrderId,
    });
    if (error) throw new SupabaseOperationError('parts.stockCheck', messageOf(error));
    return arrayOf<PartsStockShortfall>(data ?? [], 'parts.stockCheck');
  }

  async resolveStockWorkOrder(tenantId: string): Promise<StockWorkOrderHandle> {
    const { data, error } = await this.client.rpc('parts_stock_work_order', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('parts.stockWorkOrder', messageOf(error));
    return objectOf<StockWorkOrderHandle>(data, 'parts.stockWorkOrder');
  }

  // --- Lieferant: nur über den FORQ-Server ---------------------------------

  async startPunchoutSession(input: StartPunchoutInput): Promise<PunchoutSessionHandle> {
    return this.service().startPunchout(input);
  }

  async completePunchout(input: CompletePunchoutInput): Promise<SupplierPurchaseOrderRecord> {
    return this.service().completePunchout(input);
  }

  async validateOrder(input: PartsOrderActionInput): Promise<PartsOrderValidation> {
    return this.service().validate(input);
  }

  async submitOrder(input: SubmitPartsOrderInput): Promise<SupplierPurchaseOrderRecord> {
    return this.service().submit(input);
  }

  async refreshStatus(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord> {
    return this.service().refreshStatus(input);
  }

  // --- Entwurf -------------------------------------------------------------

  async createDraft(raw: CreatePartsOrderInput): Promise<SupplierPurchaseOrderRecord> {
    const input = normalizeCreatePartsOrderInput(raw);
    const { data, error } = await this.client.rpc('parts_order_create_draft', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('parts.draft.create', messageOf(error));
    return objectOf<SupplierPurchaseOrderRecord>(data, 'parts.draft.create');
  }

  async saveDraft(raw: SavePartsOrderInput): Promise<SupplierPurchaseOrderRecord> {
    const input = normalizeSavePartsOrderInput(raw);
    const { data, error } = await this.client.rpc('parts_order_save_draft', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('parts.draft.save', messageOf(error));
    return objectOf<SupplierPurchaseOrderRecord>(data, 'parts.draft.save');
  }

  async confirmDeviations(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord> {
    const { data, error } = await this.client.rpc('parts_order_confirm_deviations', {
      p_tenant_id: input.tenantId,
      p_order_id: input.purchaseOrderId,
    });
    if (error) throw new SupabaseOperationError('parts.deviations.confirm', messageOf(error));
    return objectOf<SupplierPurchaseOrderRecord>(data, 'parts.deviations.confirm');
  }

  async cancelOrder(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord> {
    const { data, error } = await this.client.rpc('parts_order_cancel', {
      p_tenant_id: input.tenantId,
      p_order_id: input.purchaseOrderId,
      p_reason: input.reason ?? '',
    });
    if (error) throw new SupabaseOperationError('parts.cancel', messageOf(error));
    return objectOf<SupplierPurchaseOrderRecord>(data, 'parts.cancel');
  }

  // --- Ansicht -------------------------------------------------------------

  async getOrder(tenantId: string, purchaseOrderId: string): Promise<SupplierPurchaseOrderRecord> {
    const { data, error } = await this.client.rpc('parts_order_get', {
      p_tenant_id: tenantId,
      p_order_id: purchaseOrderId,
    });
    if (error) throw new SupabaseOperationError('parts.order.get', messageOf(error));
    return objectOf<SupplierPurchaseOrderRecord>(data, 'parts.order.get');
  }

  async listOrders(raw: PartsOrderSearchInput): Promise<PartsOrderPage> {
    const input = normalizePartsOrderSearchInput(raw);
    const { data, error } = await this.client.rpc('parts_order_list', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('parts.order.list', messageOf(error));
    return objectOf<PartsOrderPage>(data, 'parts.order.list');
  }

  async listForWorkOrder(
    tenantId: string,
    workOrderId: string,
  ): Promise<readonly SupplierPurchaseOrderRecord[]> {
    const { data, error } = await this.client.rpc('parts_order_for_work_order', {
      p_tenant_id: tenantId,
      p_work_order_id: workOrderId,
    });
    if (error) throw new SupabaseOperationError('parts.order.forWorkOrder', messageOf(error));
    return arrayOf<SupplierPurchaseOrderRecord>(data ?? [], 'parts.order.forWorkOrder');
  }

  async listEvents(
    tenantId: string,
    purchaseOrderId: string,
  ): Promise<readonly SupplierIntegrationEventRecord[]> {
    const { data, error } = await this.client.rpc('parts_order_events', {
      p_tenant_id: tenantId,
      p_order_id: purchaseOrderId,
    });
    if (error) throw new SupabaseOperationError('parts.order.events', messageOf(error));
    return arrayOf<SupplierIntegrationEventRecord>(data ?? [], 'parts.order.events');
  }

  // --- Klärfälle und Wareneingang ------------------------------------------

  async resolveReview(input: ResolveReviewInput): Promise<SupplierPurchaseOrderRecord> {
    const { data, error } = await this.client.rpc('parts_order_resolve_review', {
      p_tenant_id: input.tenantId,
      p_order_id: input.purchaseOrderId,
      p_to_status: input.toStatus,
      p_reason: input.reason ?? '',
      p_external_order_number: input.externalOrderNumber ?? '',
    });
    if (error) throw new SupabaseOperationError('parts.review.resolve', messageOf(error));
    return objectOf<SupplierPurchaseOrderRecord>(data, 'parts.review.resolve');
  }

  async receiveGoods(raw: ReceivePartsInput): Promise<SupplierPurchaseOrderRecord> {
    const input = normalizeReceivePartsInput(raw);
    const { data, error } = await this.client.rpc('parts_order_receive', {
      p_tenant_id: input.tenantId,
      p_payload: input,
    });
    if (error) throw new SupabaseOperationError('parts.goods.receive', messageOf(error));
    return objectOf<SupplierPurchaseOrderRecord>(data, 'parts.goods.receive');
  }
}
